// Picker modal for selecting an unparented asset to attach as a child of a parent asset.
// Used on AssetDetail's "Components" card. PATCHes the child asset's parent_asset_id on selection.

import React, { useState, useEffect } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useAssetsStore from '../stores/useAssetsStore';
import { Asset } from './types';
import { AssetStatusTag } from './StatusTags';
import { Field } from '../components/ui/Field';
import FadeIn from '../components/ui/FadeIn';
import Select from '../components/ui/Select';

interface Props {
  parentId: string;
  parentDisplayCode: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddComponentModal: React.FC<Props> = ({ parentId, parentDisplayCode, onClose, onSuccess }) => {
  const api = useApi();
  const toast = useToast();
  const { types, ensureTypesLoaded } = useAssetsStore();

  const [candidates, setCandidates] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTypeId, setFilterTypeId] = useState<number | 'all'>('all');
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState<string | null>(null);

  // Warm the type cache — users deep-linking to AssetDetail may open this modal before
  // any other page has loaded the types. Without this, labels render "—" on first open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ensureTypesLoaded().catch(() => {}); }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('parent_asset_id', 'null');    // unparented only
        params.set('is_sold', 'false');
        params.set('is_active', 'true');
        params.set('is_internal_pool', 'all');    // show sellable + internal; user picks
        params.set('pageSize', '200');
        if (filterTypeId !== 'all') params.set('asset_type_id', String(filterTypeId));
        if (search) params.set('q', search);
        const result = await api.get(`/assets?${params.toString()}`);
        // Filter out the parent itself — it's unparented too, but attaching it to itself
        // would create a cycle (the DB would reject it anyway; this just hides the option).
        const filtered: Asset[] = result.data.filter((a: Asset) => a.id !== parentId);
        if (!cancelled) setCandidates(filtered);
      } catch {
        if (!cancelled) toast.error('Failed to load unassigned assets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTypeId, search, parentId]);

  const handlePick = async (child: Asset) => {
    setAssigning(child.id);
    try {
      await api.patch(`/assets/${child.id}`, { body: { parent_asset_id: parentId } });
      toast.success(`${child.display_code} attached to ${parentDisplayCode}`);
      onSuccess();
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e.message || 'Failed to attach component');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Attach Component"
      meta={`to ${parentDisplayCode}`}
      size="lg"
      footer={<button onClick={onClose} className={buttonClass('quiet', 'md')}>Close</button>}
    >
      {/* -mx-5 px-5 and the fill match the Dialog body, so rows scrolling under the filters stay hidden. */}
      <div className="sticky top-0 z-10 -mx-5 flex gap-2 bg-surface-container-low px-5 pb-3">
        <Field
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search unassigned assets…"
          aria-label="Search unassigned assets"
          className="flex-1"
        />
        <Select
          label="Asset type"
          value={String(filterTypeId)}
          onChange={key => setFilterTypeId(key === 'all' ? 'all' : parseInt(key, 10))}
          width="fit"
          options={[
            { key: 'all', label: 'All types' },
            ...types.filter(t => t.is_active).map(t => ({ key: String(t.id), label: t.label })),
          ]}
        />
      </div>

      <FadeIn key={loading ? 'loading' : candidates.length === 0 ? 'empty' : 'list'}>
      {loading && (
        <SkeletonGroup label="Loading unassigned assets" className="space-y-1">
          {[0, 1, 2, 3].map(n => <Skeleton key={n} className="h-11 w-full" />)}
        </SkeletonGroup>
      )}
      {!loading && candidates.length === 0 && (
        <EmptyState variant="area">No unassigned assets match these filters</EmptyState>
      )}
      {!loading && candidates.length > 0 && (
        <ListStack
          items={candidates}
          getKey={c => c.id}
          on="panel"
          label="Unassigned assets"
          renderRow={c => {
            const type = types.find(t => t.id === c.asset_type_id);
            return (
              <ListRow
                actions={
                  <button
                    onClick={() => handlePick(c)}
                    disabled={assigning === c.id}
                    className={buttonClass('quiet', 'sm')}
                  >
                    {assigning === c.id ? 'Attaching…' : 'Attach'}
                  </button>
                }
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="truncate font-mono text-sm text-on-surface">{c.display_code}</span>
                  <span className="truncate text-xs text-on-surface-variant">{type?.label ?? '—'}</span>
                  <AssetStatusTag status={c.operational_status} />
                </span>
              </ListRow>
            );
          }}
        />
      )}
      </FadeIn>
    </Dialog>
  );
};

export default AddComponentModal;
